/**
 * Les dimensions du plateau et des dés.
 *
 * Un module à part, et pour une raison très concrète : `lancer.ts` importe le moteur
 * physique, soit près d'un mégaoctet de WASM. Tout fichier qui lui emprunte ne
 * serait-ce qu'une constante embarque rapier avec lui — c'est arrivé, et le chunk du
 * premier affichage a quadruplé sans qu'aucun test ne bronche. La géométrie du décor
 * n'est pas de la physique : elle vit ici, et tout le monde peut la lire.
 */

export const PLATEAU = {
  /**
   * Un plateau ROND, avec un bourrelet tout autour — la piste de 421 du comptoir.
   *
   * La version précédente était une piste rectangulaire, étroite et profonde,
   * choisie pour le portrait verrouillé. Elle avait deux défauts qu'Olivier a vus
   * tout de suite sur l'instance en ligne :
   *
   *   1. **les dés paraissaient énormes.** La caméra cadrait la seule zone
   *      d'arrivée, large de trois dés et demi : un dé occupait près du tiers de
   *      l'écran. Un plateau rond se cadre en entier, et le rapport tombe à un dé
   *      pour neuf — ce qu'on voit sur une vraie table ;
   *   2. **les jets se ressemblaient tous.** Une piste étroite impose sa direction :
   *      la main partait toujours du bord proche et poussait vers le fond. Un disque
   *      n'a pas de direction privilégiée, et le geste attaque de n'importe où.
   *
   * Le rond est aussi la forme juste : on ne joue pas au 421 dans un couloir.
   */
  rayon: 4.4,
  /** Altitude de la surface sur laquelle les dés reposent. */
  surface: -0.5,
  /** Épaisseur du plateau sous la surface. */
  epaisseur: 0.1,
  /**
   * Hauteur du MUR qui retient les dés, côté physique.
   *
   * Généreuse, et sans rapport avec le bourrelet qu'on dessine : un dé lancé
   * d'un mètre et demi de haut avec de l'élan passe par-dessus un muret bas, tombe
   * hors du plateau — et ne s'immobilise **jamais**, puisqu'il n'y a rien en
   * dessous. Le solveur essaie alors tous ses gestes, les rejette tous, et finit
   * par montrer un repli de dix secondes. Rien ne signale l'erreur : le jet est
   * simplement interminable.
   */
  hauteurRebord: 2.4,
  /** Épaisseur du mur, et demi-épaisseur du bourrelet dessiné. */
  epaisseurRebord: 0.22,
  /** Hauteur du bourrelet VISIBLE. Purement décorative. */
  hauteurBourrelet: 0.46,
  /**
   * Nombre de segments du rebord côté physique.
   *
   * Rapier ne connaît pas l'anneau : on l'approche par une couronne de pavés. À 36
   * segments, la corde s'écarte du cercle de moins de deux millimètres à l'échelle
   * du plateau — invisible, et sans effet mesurable sur les rebonds.
   */
  segmentsRebord: 24,
} as const;

/**
 * Côté du dé, en unités de plateau.
 *
 * C'est LE rapport qui décide de l'allure du jeu : un dé de 1 sur un plateau de 4,4
 * de rayon paraissait énorme, et Olivier l'a redit après la mise en rond. À 0,52, un
 * dé tient dix-sept fois dans le diamètre — l'ordre de grandeur d'une vraie piste de
 * comptoir (16 mm de dé pour 30 cm de piste, soit dix-neuf).
 *
 * Le maillage, lui, reste un cube unité : la scène l'échelonne. Tout le socle
 * d'orientation (`orientation.ts`, le placement des pips) raisonne sur un cube de
 * côté 1, et le retoucher pour une question de taille serait risquer un dé qui
 * affiche la mauvaise face pour gagner deux dixièmes.
 */
export const COTE_DE = 0.52;

/** Demi-côté du dé, tel que le collider le voit. */
export const DEMI_DE = COTE_DE / 2;

